import { useQuery, useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

const MODEL_CONTENTS_QUERY_KEY = 'modelContents';
const MODEL_CONTENTS_API_URL = '/cms/modelcontents';
const MODEL_CONTENTS_SEARCH_API_URL = `${MODEL_CONTENTS_API_URL}/search`;

const searchModelContents = (modelID) =>
    Axios.post(MODEL_CONTENTS_SEARCH_API_URL, { ModelID: modelID });

const normalizeApiResponse = (response) => (response && response.data !== undefined ? response.data : response);

const normalizeModelContentsSearchResults = (response) => {
    const payload = normalizeApiResponse(response);

    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];

    if (Array.isArray(payload.Items)) return payload.Items;
    if (Array.isArray(payload.Data)) return payload.Data;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.Results)) return payload.Results;

    if (payload.ID !== undefined && payload.ID !== null) {
        return [payload];
    }

    return [];
};

const getNullableNumericFilterValue = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

const getNullableSearchTagIds = (tagIds = []) => {
    if (!Array.isArray(tagIds)) return null;

    const numericTagIds = tagIds
        .map(Number)
        .filter((tagId) => Number.isInteger(tagId) && tagId > 0);

    return numericTagIds.length ? numericTagIds : null;
};

const buildModelContentsSearchPayload = (filters = {}) => {
    const published = filters.published ?? filters.Published;

    return {
        UserCreated: getNullableNumericFilterValue(filters.userCreated ?? filters.UserCreated),
        ModelID: getNullableNumericFilterValue(filters.modelId ?? filters.ModelID),
        Published: published === undefined || published === null ? true : Boolean(published),
        Tags: getNullableSearchTagIds(filters.tagIds ?? filters.Tags),
        Text: String(filters.text ?? filters.Text ?? '').trim() || null,
    };
};

const getModelContent = (id) => Axios.get(`${MODEL_CONTENTS_API_URL}/${id}`);

const createModelContent = (data) => Axios.post(MODEL_CONTENTS_API_URL, data);

const editModelContent = (data) => {
    const { ID, ...body } = data;
    return Axios.put(`${MODEL_CONTENTS_API_URL}/${ID}`, body);
};

const deleteModelContent = ({ ID }) => Axios.delete(`${MODEL_CONTENTS_API_URL}/${ID}`);

const toggleModelContentPublished = ({ ID, Published }) =>
    Axios.put(`${MODEL_CONTENTS_API_URL}/${ID}/published`, { Published });

const invalidateModelContents = (modelID) => {
    const numericModelID = Number(modelID);
    if (!Number.isFinite(numericModelID) || numericModelID <= 0) return;

    queryClient.invalidateQueries({
        queryKey: [MODEL_CONTENTS_QUERY_KEY, numericModelID],
    });
};

const searchModelContentsApi = async (filters = {}) => {
    const response = await Axios.post(MODEL_CONTENTS_SEARCH_API_URL, buildModelContentsSearchPayload(filters));
    return normalizeModelContentsSearchResults(response);
};

const saveModelContent = async (data) => {
    const response = data.ID ? await editModelContent(data) : await createModelContent(data);
    invalidateModelContents(data.ModelID);
    return response;
};

const useModelContents = (modelID) => {
    const disableQuery = !modelID || isNaN(parseInt(modelID));
    const intID = !disableQuery ? parseInt(modelID) : null;
    return useQuery({
        queryKey: [MODEL_CONTENTS_QUERY_KEY, intID],
        queryFn: () => searchModelContents(intID),
        staleTime: 60000,
        cacheTime: 60000,
        enabled: !disableQuery,
    });
};

const useAddModelContent = () => {
    return useMutation({
        mutationFn: createModelContent,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [MODEL_CONTENTS_QUERY_KEY, Number(variables.ModelID)],
            });
        },
    });
};

const useEditModelContent = () => {
    return useMutation({
        mutationFn: editModelContent,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [MODEL_CONTENTS_QUERY_KEY, Number(variables.ModelID)],
            });
        },
    });
};

const useRemoveModelContent = () => {
    return useMutation({
        mutationFn: deleteModelContent,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [MODEL_CONTENTS_QUERY_KEY, Number(variables.ModelID)],
            });
        },
    });
};

const useToggleModelContentPublished = () => {
    return useMutation({
        mutationFn: toggleModelContentPublished,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [MODEL_CONTENTS_QUERY_KEY, Number(variables.ModelID)],
            });
        },
    });
};

export const loadModelContentById = (id) => getModelContent(id);
export { searchModelContentsApi, saveModelContent };

export {
    useModelContents,
    useAddModelContent,
    useEditModelContent,
    useRemoveModelContent,
    useToggleModelContentPublished,
};
